import { useTRPC } from "@/trpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, AlertCircle, Briefcase, CheckCircle } from "lucide-react";
import { useState } from "react";
import {
  showNotInterestedToast,
  showNotInterestedErrorToast,
} from "../utils/show-not-interested-toast";
import { JobAcceptDialog, type AcceptableJob } from "../job-accept-dialog";
import { JobPostRemoveInterestDialog } from "../job-post-remove-interest-dialog";
import { Link } from "@tanstack/react-router";
import { buttonVariants } from "@/components/ui/button";
import { useMarkJobPostViewed } from "../use-mark-job-post-viewed";
import { RecommendedJobItem } from "./job-post-recommended/recommended-job-item";
import { RecommendedJobsSkeleton } from "./job-post-recommended/recommended-jobs-skeleton";

/** How many recommendations are shown at once; the rest queue up behind them. */
const MAX_VISIBLE_RECOMMENDATIONS = 5;

export function JobPostRecommended() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const {
    data: jobs,
    isLoading,
    error,
  } = useQuery(trpc.jobPost.recommended.queryOptions());
  const { mutateAsync: setDecision } = useMutation(
    trpc.jobPost.setDecision.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          trpc.jobPost.recommended.queryOptions(),
        );
      },
    }),
  );

  const [acceptedJob, setAcceptedJob] = useState<AcceptableJob | null>(null);
  const [removeInterestJobId, setRemoveInterestJobId] = useState<string | null>(
    null,
  );
  const [removeInterestPreviousDecision, setRemoveInterestPreviousDecision] =
    useState<boolean | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [leavingJobIds, setLeavingJobIds] = useState<string[]>([]);
  const [leavingJobsList, setLeavingJobsList] = useState(jobs);
  const markViewed = useMarkJobPostViewed();

  // A leaving job stays collapsed only until a fetched list no longer has it.
  // Dropping it then means a job that later comes back (e.g. its decision was
  // undone in another tab) shows again instead of staying collapsed. This runs
  // during render, React's pattern for adjusting state when its inputs change.
  if (jobs !== leavingJobsList) {
    setLeavingJobsList(jobs);
    setLeavingJobIds((ids) =>
      ids.filter((id) => jobs?.some((job) => job.id === id)),
    );
  }

  // Starts the collapse animation for a decided job; the refetch that follows
  // removes it from the list.
  const markLeaving = (jobId: string) => {
    setLeavingJobIds((ids) => (ids.includes(jobId) ? ids : [...ids, jobId]));
  };
  const unmarkLeaving = (jobId: string) => {
    setLeavingJobIds((ids) => ids.filter((id) => id !== jobId));
  };

  // --- Event Handlers ---
  const handleInterestChange = async ({
    jobId,
    isInterested,
  }: {
    jobId: string;
    isInterested: boolean | null;
  }) => {
    setHasInteracted(true);
    if (isInterested === false) {
      markLeaving(jobId);
      try {
        await setDecision({ id: jobId, interested: false });
        showNotInterestedToast();
      } catch {
        unmarkLeaving(jobId);
        showNotInterestedErrorToast();
      }
      return;
    }
    const job = jobs?.find((j) => j.id === jobId);
    if (isInterested === true) {
      setAcceptedJob({
        id: jobId,
        externalUrl: job?.externalUrl ?? null,
        eligibility: job?.eligibility,
      });
      return;
    }
    if (isInterested === null) {
      setRemoveInterestJobId(jobId);
      setRemoveInterestPreviousDecision(job?.decision?.interested ?? null);
      return;
    }
  };

  const refreshRecommendations = () => {
    queryClient.invalidateQueries(trpc.jobPost.recommended.queryOptions());
  };

  const handleAcceptSuccess = () => {
    if (acceptedJob) {
      markLeaving(acceptedJob.id);
    }
    refreshRecommendations();
  };

  const handleRemoveInterestSuccess = () => {
    if (removeInterestJobId) {
      markLeaving(removeInterestJobId);
    }
    refreshRecommendations();
  };

  if (isLoading) {
    return <RecommendedJobsSkeleton />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full px-6">
        <div className="text-center max-w-md">
          <AlertCircle className="h-8 w-8 mx-auto text-red-500 mb-4" />
          <p className="text-red-600 text-lg">
            Unable to load job recommendations
          </p>
          <p className="text-slate-500 text-sm mt-2">Please try again later</p>
        </div>
      </div>
    );
  }

  if (!jobs || jobs.length === 0) {
    if (hasInteracted) {
      return (
        <div className="mx-auto flex max-w-xl items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-12 shadow-sm">
          <div className="text-center max-w-md">
            <div className="mb-6">
              <CheckCircle className="mx-auto h-16 w-16 rounded-2xl bg-green-50 p-3 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              All done! 🎉
            </h2>
            <p className="text-xl text-slate-700 mb-2 font-medium">
              You've reviewed your job recommendations
            </p>
            <p className="text-slate-500">
              We'll notify you when jobs that match your interests become
              available
            </p>
            <div className="mt-8">
              <Link
                to="/provider/jobs/search"
                className={buttonVariants({ className: "gap-2" })}
              >
                <Search className="h-4 w-4" />
                Search all jobs
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="mx-auto flex max-w-xl flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-12 text-center shadow-sm">
        <div className="w-16 h-16 rounded-2xl bg-primary/5 flex items-center justify-center mb-6">
          <Briefcase className="h-8 w-8 text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">
          No Recommendations Yet
        </h2>
        <p className="text-base text-slate-500 max-w-md mb-8">
          We're working on finding perfect jobs for you. We'll notify you when
          jobs that match your interests become available.
        </p>
        <Link
          to="/provider/jobs/search"
          className={buttonVariants({ className: "gap-2" })}
        >
          <Search className="h-5 w-5" />
          Search All Jobs
        </Link>
      </div>
    );
  }

  const visibleJobs = jobs.slice(0, MAX_VISIBLE_RECOMMENDATIONS);
  const waitingCount = jobs.length - visibleJobs.length;

  let summaryText: string;
  if (waitingCount > 0) {
    summaryText = `Showing ${visibleJobs.length} of ${jobs.length} matches`;
  } else if (jobs.length === 1) {
    summaryText = "1 match to review";
  } else {
    summaryText = `${jobs.length} matches to review`;
  }

  return (
    <>
      <div className="mx-auto max-w-xl">
        <header className="mb-4 px-1">
          <h1 className="text-2xl font-bold text-slate-900">
            Recommended for you
          </h1>
          <p className="mt-1 text-sm text-slate-600" aria-live="polite">
            {summaryText}
          </p>
        </header>
        <ul>
          {visibleJobs.map((job) => (
            <RecommendedJobItem
              key={job.id}
              job={job}
              isLeaving={leavingJobIds.includes(job.id)}
              onInterestChange={handleInterestChange}
              onBottomSeen={markViewed}
            />
          ))}
        </ul>
        {waitingCount > 0 && (
          <p className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-center text-sm text-slate-600">
            {waitingCount} more{" "}
            {waitingCount === 1 ? "match is" : "matches are"} waiting. They'll
            appear here as you respond to these.
          </p>
        )}
      </div>
      {acceptedJob && (
        <JobAcceptDialog
          job={acceptedJob}
          onClose={() => setAcceptedJob(null)}
          onSuccess={handleAcceptSuccess}
        />
      )}
      {removeInterestJobId && (
        <JobPostRemoveInterestDialog
          onClose={() => {
            setRemoveInterestJobId(null);
            setRemoveInterestPreviousDecision(null);
          }}
          onSuccess={handleRemoveInterestSuccess}
          jobId={removeInterestJobId}
          previousDecision={removeInterestPreviousDecision ?? undefined}
        />
      )}
    </>
  );
}
